import React, { createContext, forwardRef, useContext } from "react";
import ReactModal from "react-modal";
import { cn, getBackgroundStyles, getForegroundStyles } from "../../styles";
import { LocalizationScope } from "../../util/LocalizationScope";
import { hasValue } from "../../helper";
import { PanelColor, StyledContainerProps } from "../../props";
import { Typography, TypographyH2Props } from "../../atoms";
import { ScrollArea } from "../ScrollArea";

const DialogContext = createContext(null);

// TODO: adapt for dynamic key seperator
const SCOPE = ["", "::PSS::Dialog"];

const useDialogContext = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("Dialog components must be used within a Dialog");
  }
  return context;
};

export interface DialogTitleProps extends TypographyH2Props {}

/**
 * Dialog title component that automatically sets up accessibility attributes.
 * Renders as an h2 element with proper ARIA labeling for the dialog.
 */
export const DialogTitle = forwardRef<HTMLHeadingElement, DialogTitleProps>(
  (p, ref) => {
    const { dialogId } = useDialogContext();
    return (
      <Typography.H2
        {...p}
        id={dialogId && `${dialogId}-title`}
        ref={ref}
        className={cn("text-xl", p.className)}
      />
    );
  },
);

DialogTitle.displayName = "Dialog.Title";

export interface DialogContentProps extends StyledContainerProps {
  /**
   * Adds border separators above and below the content area.
   * Useful for visually separating content from header/footer.
   * @default false
   */
  separated?: boolean;

  /**
   * Renders the content in a scroll area.
   * Disable for intrinsically sized content (e.g. a calendar): the scroll
   * area's content is absolutely positioned and therefore does not
   * contribute to the auto width of the dialog.
   * @default true
   */
  scroll?: boolean;
}

/**
 * Dialog content area component with optional visual separation.
 * Provides scrollable content area that grows to fill available space.
 */
export const DialogContent = ({
  children,
  className,
  separated = false,
  scroll = true,
}: DialogContentProps) => {
  const classes = cn(
    "px-3 py-1 flex-1 h-full",
    separated &&
      "border-t first:border-t-0 border-b last:border-b-0 border-front-200 dark:border-front-700",
    className,
  );
  return scroll ? (
    <ScrollArea className={classes}>
      {children}
    </ScrollArea>
  ) : (
    <div className={classes}>{children}</div>
  );
};

DialogContent.displayName = "Dialog.Content";

/**
 * Dialog actions component for buttons and interactive elements.
 * Typically used in the footer area with right-aligned action buttons.
 */
export const DialogActions = ({
  children,
  className,
}: StyledContainerProps) => {
  return (
    <div className={cn("grow flex justify-end gap-2", className)}>
      {children}
    </div>
  );
};

DialogActions.displayName = "Dialog.Actions";

/**
 * Dialog hint component for displaying small helper text.
 * Renders with muted styling and reduced font size.
 */
export const DialogHint = ({ children, className }: StyledContainerProps) => {
  return (
    <p
      className={cn(
        "self-center -mb-[3px] text-[0.6rem] text-front-400 dark:text-front-400",
        className,
      )}
    >
      {children}
    </p>
  );
};

DialogHint.displayName = "Dialog.Hint";

/**
 * Dialog header component for title and top-level controls.
 * Provides consistent spacing and layout for dialog headers.
 */
export const DialogHeader = ({ children, className }: StyledContainerProps) => {
  return (
    <div
      className={cn("px-3 py-2 flex items-center justify-between", className)}
    >
      {children}
    </div>
  );
};

DialogHeader.displayName = "Dialog.Header";

/**
 * Dialog footer component for actions and bottom controls.
 * Provides consistent spacing and layout for dialog footers.
 */
export const DialogFooter = ({ children, className }: StyledContainerProps) => {
  return (
    <div className={cn("px-3 py-2 flex justify-between", className)}>
      {children}
    </div>
  );
};

DialogFooter.displayName = "Dialog.Footer";

export interface DialogProps extends StyledContainerProps {
  /**
   * Controls whether the dialog is visible.
   */
  open: boolean;

  /**
   * Callback function called when the dialog should be closed.
   * Triggered by escape key, backdrop click, or programmatic close.
   */
  onClose: () => void;

  /**
   * Fixed width of the dialog.
   * Can be a CSS string (e.g., "400px", "50%") or number (pixels).
   */
  width?: string | number;

  /**
   * Fixed height of the dialog.
   * Can be a CSS string (e.g., "300px", "80vh") or number (pixels).
   */
  height?: string | number;

  /**
   * Minimum width of the dialog.
   * Can be a CSS string (e.g., "300px", "25%") or number (pixels).
   * @default 750 (if no width is specified)
   */
  minWidth?: string | number;

  /**
   * Minimum height of the dialog.
   * Can be a CSS string (e.g., "200px", "30vh") or number (pixels).
   */
  minHeight?: string | number;

  /**
   * Unique identifier for the dialog.
   * Used for accessibility attributes and ARIA labeling.
   */
  dialogId?: string;

  /**
   * Applies blur effect to the backdrop behind the dialog.
   * @default false
   */
  blurBackdrop?: boolean;

  /**
   * Allows closing the dialog by pressing the Escape key.
   * @default true
   */
  closeOnEscape?: boolean;

  /**
   * Allows closing the dialog by clicking on the backdrop.
   * @default true
   */
  closeOnBackdropClick?: boolean;

  /**
   * Panel color level for the dialog background.
   * @default PanelColor.level0
   */
  color?: PanelColor;

  /**
   * Keeps the dialog content mounted in the DOM when closed.
   * Useful for preserving form state or avoiding re-initialization.
   * @default false
   * @todo Implementation pending - mount content hidden to enable this feature
   */
  // keepMounted?: boolean;
}

/**
 * Flexible and accessible Dialog component built on react-modal.
 * Supports modal dialogs with customizable dimensions, backdrop behavior,
 * and keyboard/mouse interaction controls. Includes sub-components for
 * structured dialog content (Header, Title, Content, Actions, Footer, Hint).
 */
export const Dialog = ({
  children,
  className,
  open,
  onClose,
  width,
  height,
  minWidth,
  minHeight,
  dialogId,
  //TODO: mount content hidden to enable keepMounted prop
  // keepMounted = false,
  blurBackdrop = false,
  closeOnEscape = true,
  closeOnBackdropClick = true,
  color,
}: DialogProps) => {
  return (
    <ReactModal
      isOpen={open}
      onRequestClose={onClose}
      shouldCloseOnEsc={closeOnEscape}
      shouldCloseOnOverlayClick={closeOnBackdropClick}
      contentLabel={dialogId}
      closeTimeoutMS={200}
      aria={{
        labelledby: dialogId ? `${dialogId}-title` : undefined,
      }}
      style={{
        content: {
          width,
          height: height || (hasValue(minHeight) ? 0 : "auto"),
          minWidth: hasValue(minWidth) ? minWidth : hasValue(width) ? 0 : 750,
          minHeight,
          outline: "none",
        },
      }}
      overlayClassName={{
        base: cn(
          "fixed inset-0",
          "flex items-center justify-center",
          "bg-back-950/50 dark:bg-black/80",
          "opacity-0 transition-opacity duration-200 ease-in-out",
          blurBackdrop && "backdrop-blur-xs",
          "z-[1400]",
        ),
        afterOpen: "opacity-100",
        beforeClose: "!opacity-0",
      }}
      className={cn(
        color
          ? [getBackgroundStyles(color), getForegroundStyles(color)]
          : [
              "bg-back-50 text-front-900",
              "dark:bg-back-900 dark:text-front-50",
            ],
        "rounded-lg shadow-lg",
        "flex flex-col",
        "border-0 p-0",
        "m-8",
        "max-h-[calc(100vh-4rem)]",
        "overflow-hidden",
        "z-[1500]",
        className,
      )}
    >
      <DialogContext.Provider value={{ dialogId }}>
        <LocalizationScope scope={SCOPE}>{children}</LocalizationScope>
      </DialogContext.Provider>
    </ReactModal>
  );
};
Dialog.displayName = "Dialog";

Dialog.Title = DialogTitle;
Dialog.Content = DialogContent;
Dialog.Actions = DialogActions;
Dialog.Hint = DialogHint;
Dialog.Header = DialogHeader;
Dialog.Footer = DialogFooter;
